'use client'

import { ReactElement, ReactNode } from 'react'

import { Dialog } from '@syconium/little-miss-figgy/dist/components/Dialog'

import { useNavSubMenuDialogState } from './NavSubMenuDialogProvider'
import * as Styles from './styles'

const NavSubMenuDialogHeader = ({
  backLabel,
  closeLabel,
  onCloseNav,
  title,
}: {
  backLabel: string
  closeLabel: string
  onCloseNav: () => void
  title: string
}) => {
  const dialog = Dialog.useState()

  return (
    <Styles.Header>
      {/*
        Ariakit focuses the first dismiss in a dialog on open, which lands focus on "back" — the
        action that undoes the navigation the shopper just made.
      */}
      <Dialog.Dismiss aria-label={backLabel} render={<Styles.HeaderButton />}>
        <Styles.BackIcon aria-hidden='true' />
      </Dialog.Dismiss>

      {title}

      <Styles.HeaderButton
        type='button'
        aria-label={closeLabel}
        onClick={() => {
          // Hide this dialog too so both panels animate out together instead of leaving this one
          // visible on top while the nav behind it closes.
          onCloseNav()
          dialog.hide()
        }}
      >
        <Styles.CloseIcon aria-hidden='true' />
      </Styles.HeaderButton>
    </Styles.Header>
  )
}

export type NavSubMenuDialogProps = {
  title: string
  trigger: ReactElement
  backLabel: string
  closeLabel: string
  onCloseNav: () => void
  className?: string
  children?: ReactNode
}

export const NavSubMenuDialog = ({
  backLabel,
  children,
  className,
  closeLabel,
  onCloseNav,
  title,
  trigger,
}: NavSubMenuDialogProps) => {
  const { setIsSubMenuOpen } = useNavSubMenuDialogState()

  return (
    <Dialog.Provider setOpen={setIsSubMenuOpen}>
      <Dialog.Disclosure render={trigger} />

      <Styles.Drawer
        dir='rtl'
        analyticsTitle={`Navigation: ${title}`}
        // The nav underneath already dims the page, and this panel covers the viewport on mobile,
        // so a second backdrop would only double-darken the edges.
        backdrop={false}
        className={className}
        header={
          <NavSubMenuDialogHeader
            backLabel={backLabel}
            closeLabel={closeLabel}
            onCloseNav={onCloseNav}
            title={title}
          />
        }
        content={<Styles.Body />}
      >
        {children}
      </Styles.Drawer>
    </Dialog.Provider>
  )
}
